/**
 * FERP PDF Ink Canvas (DRR-079)
 *
 * Freehand drawing overlay using PointerEvent + Bezier smoothing.
 * Captures strokes as arrays of PdfPoint, stores in PdfAnnotation.inkPaths.
 */

import { useRef, useCallback, useState, type PointerEvent } from 'react';
import { Box } from '@mantine/core';
import { useAuth } from '@/auth';
import { usePdfSuite } from './PdfSuiteContext';
import type { PdfPoint } from './types';

interface InkCanvasProps {
  page: number;
  scale: number;
}

export function InkCanvas({ page, scale }: InkCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState<PdfPoint[]>([]);
  const [strokes, setStrokes] = useState<PdfPoint[][]>([]);
  const { state, addAnnotation } = usePdfSuite();
  const { user } = useAuth();

  const getRelativePoint = useCallback(
    (e: PointerEvent): PdfPoint | null => {
      const el = overlayRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    },
    [],
  );

  const drawStroke = useCallback(
    (ctx: CanvasRenderingContext2D, points: PdfPoint[], w: number, h: number, color: string, lineWidth: number) => {
      if (points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const absPoints = points.map((p) => ({ x: p.x * w, y: p.y * h }));
      ctx.moveTo(absPoints[0]!.x, absPoints[0]!.y);

      // Bezier smoothing
      for (let i = 1; i < absPoints.length - 1; i++) {
        const curr = absPoints[i]!;
        const next = absPoints[i + 1]!;
        const cx = (curr.x + next.x) / 2;
        const cy = (curr.y + next.y) / 2;
        ctx.quadraticCurveTo(curr.x, curr.y, cx, cy);
      }

      const last = absPoints[absPoints.length - 1]!;
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
    },
    [],
  );

  const redrawCanvas = useCallback(
    (allStrokes: PdfPoint[][], active?: PdfPoint[]) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      for (const stroke of allStrokes) {
        drawStroke(ctx, stroke, w, h, state.viewer.inkColor, state.viewer.inkStrokeWidth);
      }
      if (active && active.length > 1) {
        drawStroke(ctx, active, w, h, state.viewer.inkColor, state.viewer.inkStrokeWidth);
      }
    },
    [drawStroke, state.viewer.inkColor, state.viewer.inkStrokeWidth],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (state.viewer.annotationMode !== 'ink') return;
      const pt = getRelativePoint(e);
      if (!pt) return;
      setIsDrawing(true);
      setCurrentStroke([pt]);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [state.viewer.annotationMode, getRelativePoint],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDrawing) return;
      const pt = getRelativePoint(e);
      if (!pt) return;
      setCurrentStroke((prev) => {
        const updated = [...prev, pt];
        redrawCanvas(strokes, updated);
        return updated;
      });
    },
    [isDrawing, getRelativePoint, strokes, redrawCanvas],
  );

  const handlePointerUp = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (currentStroke.length < 2) {
      setCurrentStroke([]);
      return;
    }

    const allStrokes = [...strokes, currentStroke];
    setStrokes(allStrokes);
    setCurrentStroke([]);
    redrawCanvas(allStrokes);

    // Calculate bounding rect of all strokes
    const allPoints = allStrokes.flat();
    const xs = allPoints.map((p) => p.x);
    const ys = allPoints.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    addAnnotation({
      id: `pending_${Date.now()}`,
      type: 'ink',
      page,
      rect: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
      content: '',
      color: state.viewer.inkColor,
      strokeWidth: state.viewer.inkStrokeWidth,
      inkPaths: allStrokes,
      createdBy: user?.id ?? 'unknown',
      createdAt: new Date().toISOString(),
    });

    // Reset local strokes after committing
    setStrokes([]);
  }, [isDrawing, currentStroke, strokes, page, state.viewer, addAnnotation, user?.id, redrawCanvas]);

  // Resize canvas to match overlay
  const handleOverlayRef = useCallback(
    (el: HTMLDivElement | null) => {
      (overlayRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (el && canvasRef.current) {
        const canvas = canvasRef.current;
        canvas.width = el.offsetWidth;
        canvas.height = el.offsetHeight;
      }
    },
    [],
  );

  if (state.viewer.annotationMode !== 'ink') return null;

  return (
    <Box
      ref={handleOverlayRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        cursor: 'crosshair',
        zIndex: 15,
        touchAction: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    </Box>
  );
}
